import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useRouteContext } from '@tanstack/react-router';
import { Button, ConfigProvider, Empty, Flex, Grid, Modal, Typography, message } from 'antd';
import { FileText, LayoutList, PauseCircle, PlayCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import z from 'zod';

import {
  useBulkArchiveAiAgents,
  useDisableAgentWebhook,
  useEnableAgentWebhook,
} from '../../../../api';
import { patchRecordInCache } from '../../../../api/optimisticCache';
import { usePermissions } from '../../../../modules/account/hooks';
import {
  AgentTable,
  buildAgentEmptyPreviewRows,
  useBulkSetAiAgentStateMutation,
  useListAiAgentsQuery,
  useUpdateAiAgentMutation,
} from '../../../../modules/ai-agent';
import { useAiAssistantPanel, useAiUiSnapshot } from '../../../../modules/ai-next/hooks';
import { useAiAssistant } from '../../../../modules/ai-next/providers';
import { GlobalSearch } from '../../../../modules/global-search/components';
import { HeaderActions } from '../../../../modules/navigation/components/header-actions';
import { SelectionActionsBar } from '../../../../modules/navigation/components/selection-actions-bar';
import { useAgentRunsAction } from '../../../../modules/navigation/hooks';
import { CrmEmptyStateLayout } from '../../../../modules/records/components/empty-states';
import { DateRangeFilter, resolveDateRange } from '../../../../ui/components/date-range-filter';
import { NavbarTab, NavbarTabsOverflow } from '../../../../ui/components/navbar-tabs';
import { SidebarSection } from '../../../../ui/components/navigation';
import { TableSurface } from '../../../../ui/components/table-surface';
import { useDensity, useNavigationPosition } from '../../../../ui/hooks';
import { useLayoutInsets } from '../../../../ui/providers/layout-insets';

import type {
  ListAiAgentsResponseDtoItemsItemState,
  ListAiAgentsResponseDtoItemsItemTriggersItem,
} from '../../../../api';
import type { AiAgentSort } from '../../../../modules/ai-agent';

const AgentsRouteSearchParamsSchema = z.object({
  state: z.enum(['active', 'paused', 'draft']).optional(),
  sortField: z.enum(['name', 'createdAt', 'updatedAt']).optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  datePreset: z.string().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
});

const EMPTY_SELECTED_ROWS = new Set<string>();

const AGENT_AI_COLUMNS = [
  { id: 'name', label: 'Agent' },
  { id: 'state', label: 'Status' },
  { id: 'triggers', label: 'Triggers' },
  { id: 'webhook', label: 'Webhook' },
  { id: 'errors', label: 'Errors' },
  { id: 'successRate', label: 'Success rate' },
  { id: 'credits', label: 'Credits' },
];

function summarizeAgentTriggers(triggers: ListAiAgentsResponseDtoItemsItemTriggersItem[]): string {
  return triggers
    .map((trigger) => ('event' in trigger ? trigger.event : `${trigger.type}:${trigger.condition}`))
    .join(', ');
}

const AgentsIndexRoute = () => {
  const navigate = useNavigate({ from: '/agents' });
  const insets = useLayoutInsets();
  const { isSideNav } = useNavigationPosition();
  const { density, toolbarTheme } = useDensity('grid');
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const hasSidebarGutter = isSideNav && !isMobile;
  const { selectedOrganization } = useRouteContext({ from: '/_authenticated/_app' });
  const search = Route.useSearch();
  const { state, sortField, sortDirection } = search;
  const {
    preset: datePreset,
    from: dateFrom,
    to: dateTo,
  } = resolveDateRange({ datePreset: '7d', ...search });
  const { panel, uiContext } = useAiAssistant();
  const { isOpen: isAiPanelOpen } = useAiAssistantPanel();
  const { can } = usePermissions();
  const canCreateAgents = can('agents', 'create');
  const canEditAgents = can('agents', 'edit');
  const canDeleteAgents = can('agents', 'delete');

  const sort: AiAgentSort | undefined =
    sortField && sortDirection ? { field: sortField, direction: sortDirection } : undefined;

  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useListAiAgentsQuery(selectedOrganization.id, state, sort, {
      from: dateFrom.startOf('day').toISOString(),
      to: dateTo.endOf('day').toISOString(),
    });

  const { data: orgAgentsData, isPending: isOrgAgentsPending } = useListAiAgentsQuery(
    selectedOrganization.id,
    undefined,
    undefined,
  );

  const orgHasAgents = useMemo(() => {
    const first = orgAgentsData?.pages?.[0]?.data;
    return (first?.total ?? 0) > 0 || (first?.items?.length ?? 0) > 0;
  }, [orgAgentsData]);

  const queryClient = useQueryClient();
  const updateAiAgentMutation = useUpdateAiAgentMutation(selectedOrganization.id);
  const bulkArchiveAiAgentsMutation = useBulkArchiveAiAgents();
  const setAgentsStateMutation = useBulkSetAiAgentStateMutation(selectedOrganization.id);

  const [webhookBusyAgentId, setWebhookBusyAgentId] = useState<string | null>(null);
  const invalidateAgentList = useCallback(() => {
    queryClient
      .invalidateQueries({
        queryKey: ['ai-agents', selectedOrganization.id],
      })
      .catch(() => {});
  }, [queryClient, selectedOrganization.id]);

  const { mutate: enableWebhook } = useEnableAgentWebhook({
    mutation: {
      onMutate: ({ aiAgentId }) => {
        setWebhookBusyAgentId(aiAgentId);

        return patchRecordInCache(queryClient, aiAgentId, { webhookEnabled: true });
      },
      onSuccess: () => {
        message.success('Webhook enabled');
        invalidateAgentList();
      },
      onError: () => message.error('Failed to enable webhook'),
      onSettled: () => setWebhookBusyAgentId(null),
    },
  });

  const { mutate: disableWebhook } = useDisableAgentWebhook({
    mutation: {
      onMutate: ({ aiAgentId }) => {
        setWebhookBusyAgentId(aiAgentId);

        return patchRecordInCache(queryClient, aiAgentId, { webhookEnabled: false });
      },
      onSuccess: () => {
        message.success('Webhook disabled');
        invalidateAgentList();
      },
      onError: () => message.error('Failed to disable webhook'),
      onSettled: () => setWebhookBusyAgentId(null),
    },
  });

  const agents = useMemo(() => data?.pages.flatMap((page) => page.data.items ?? []) ?? [], [data]);

  const filterValue = state ?? 'all';

  const hasListFilters = filterValue !== 'all' || Boolean(sortField && sortDirection);
  const activeSelectionKey = `${filterValue}|${sortField ?? ''}|${sortDirection ?? ''}`;
  const [selectedRowsState, setSelectedRowsState] = useState<{
    key: string;
    rows: Set<string>;
  }>({
    key: activeSelectionKey,
    rows: new Set<string>(),
  });
  const selectedRows =
    selectedRowsState.key === activeSelectionKey ? selectedRowsState.rows : EMPTY_SELECTED_ROWS;
  const handleSelectedRowsChange = useCallback(
    (rows: Set<string>) => {
      setSelectedRowsState({
        key: activeSelectionKey,
        rows,
      });
    },
    [activeSelectionKey],
  );

  const isAgentsFilteredEmpty =
    !isPending && !isError && orgHasAgents && agents.length === 0 && hasListFilters;

  const isResolvingOrgAgents =
    !isPending && !isError && agents.length === 0 && filterValue === 'all' && isOrgAgentsPending;

  const tableIsPending = isPending || isResolvingOrgAgents;

  const displayAgents = useMemo(() => {
    if (isPending || isError || isResolvingOrgAgents) return [];
    if (agents.length > 0) return agents;
    if (isAgentsFilteredEmpty) return [];
    if (!orgHasAgents && filterValue === 'all') return buildAgentEmptyPreviewRows();
    return [];
  }, [
    agents,
    isPending,
    isError,
    isResolvingOrgAgents,
    isAgentsFilteredEmpty,
    orgHasAgents,
    filterValue,
  ]);

  const isEmptyPreview =
    !isPending &&
    !isError &&
    !orgHasAgents &&
    filterValue === 'all' &&
    agents.length === 0 &&
    displayAgents.length > 0;

  const aiVisibleAgentIds = useMemo(() => agents.map((agent) => agent.id), [agents]);
  const aiScreenRecords = useMemo(
    () =>
      agents.map((agent) => {
        const { completed = 0, blocked = 0, failed = 0 } = agent.runStats ?? {};
        const finished = completed + blocked + failed;
        const successRate = finished > 0 ? `${Math.round((completed / finished) * 100)}%` : '—';
        return {
          id: agent.id,
          values: {
            name: agent.name,
            state: agent.state,
            triggers: summarizeAgentTriggers(agent.triggers),
            webhook: agent.webhookEnabled ? 'enabled' : 'disabled',
            errors: String(failed),
            successRate,
            credits: String(agent.credits ?? 0),
          },
        };
      }),
    [agents],
  );

  useAiUiSnapshot({
    entity: 'agent',
    visibleCount: aiVisibleAgentIds.length,
    columns: AGENT_AI_COLUMNS,
    records: aiScreenRecords,
    total: data?.pages?.[0]?.data.total ?? agents.length,
    hasMore: hasNextPage ?? false,
    enabled: agents.length > 0,
  });

  const showAgentsNoDataFull =
    !isPending &&
    !isError &&
    !isOrgAgentsPending &&
    !orgHasAgents &&
    agents.length === 0 &&
    filterValue !== 'all';
  const selectedAgents = useMemo(
    () =>
      displayAgents.filter((agent) => selectedRows.has(agent.id) && !('__previewKind' in agent)),
    [displayAgents, selectedRows],
  );
  const selectedCount = selectedAgents.length;

  useEffect(() => {
    if (selectedCount > 0) {
      uiContext.clearScopedFlow();
      uiContext.setSelection({ entity: 'agent', ids: selectedAgents.map((agent) => agent.id) });
    }
    panel.sync();

    return uiContext.clearSelection;
  }, [selectedAgents, selectedCount, panel, uiContext]);

  const handleStatusChange = useCallback(
    (nextValue: unknown) => {
      if (nextValue === 'all') {
        navigate({ search: {} });
        return;
      }

      navigate({ search: { state: nextValue as ListAiAgentsResponseDtoItemsItemState } });
    },
    [navigate],
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleSort = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      const validField = field as 'name' | 'createdAt' | 'updatedAt';
      navigate({
        search: (prev) => ({
          ...prev,
          sortField: validField,
          sortDirection: direction,
        }),
      });
    },
    [navigate],
  );

  const handleClearSort = useCallback(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        sortField: undefined,
        sortDirection: undefined,
      }),
    });
  }, [navigate]);

  const handleUpdateAiAgent = useCallback(
    (params: {
      aiAgentId: string;
      data: {
        name?: string;
        triggers?: ListAiAgentsResponseDtoItemsItemTriggersItem[];
        state?: 'active' | 'paused';
      };
    }) => {
      updateAiAgentMutation.mutate(params);
    },
    [updateAiAgentMutation],
  );

  const handleEnableWebhook = useCallback(
    (aiAgentId: string) => {
      enableWebhook({ organizationId: selectedOrganization.id, aiAgentId });
    },
    [enableWebhook, selectedOrganization.id],
  );

  const handleDisableWebhook = useCallback(
    (aiAgentId: string) => {
      disableWebhook({ organizationId: selectedOrganization.id, aiAgentId });
    },
    [disableWebhook, selectedOrganization.id],
  );

  const handleNameClick = useCallback(
    (aiAgentId: string) => {
      navigate({ to: '/agents/$agentId', params: { agentId: aiAgentId } });
    },
    [navigate],
  );

  const handleNewAgent = useCallback(() => {
    navigate({ to: '/agents/new' });
  }, [navigate]);

  const handleClearAgentsFilters = useCallback(() => {
    navigate({ search: {} }).catch(() => {});
  }, [navigate]);

  const runsAction = useAgentRunsAction({
    organizationId: selectedOrganization.id,
    agent: selectedAgents.length === 1 ? selectedAgents[0] : undefined,
  });

  const handleSetAgentsState = useCallback(
    (state: 'active' | 'paused') => {
      setAgentsStateMutation.mutate(
        { aiAgentIds: selectedAgents.map((agent) => agent.id), state },
        {
          onSuccess: () => {
            message.success(state === 'active' ? 'Agents resumed' : 'Agents paused');
            handleSelectedRowsChange(new Set());
          },
          onError: () => message.error(state === 'active' ? "Couldn't resume" : "Couldn't pause"),
        },
      );
    },
    [handleSelectedRowsChange, selectedAgents, setAgentsStateMutation],
  );

  const handleBulkArchiveAgents = useCallback(() => {
    if (selectedCount === 0) {
      return;
    }

    Modal.confirm({
      title: `Archive ${selectedCount} agent(s)?`,
      content: 'The selected agents will be archived and hidden from the active list.',
      okText: 'Archive',
      okButtonProps: { danger: true, 'data-testid': 'confirm' },
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const result = await bulkArchiveAiAgentsMutation.mutateAsync({
            organizationId: selectedOrganization.id,
            data: { ids: selectedAgents.map((agent) => agent.id) },
          });
          handleSelectedRowsChange(new Set());
          message.success(`Archived ${result.data.archivedCount} agent(s)`);
        } catch {
          message.error('Failed to archive agents');
        }

        invalidateAgentList();
      },
    });
  }, [
    bulkArchiveAiAgentsMutation,
    selectedAgents,
    handleSelectedRowsChange,
    invalidateAgentList,
    selectedOrganization.id,
    selectedCount,
  ]);

  const statusOptions = [
    { id: 'all', name: 'All', icon: <LayoutList size={12} />, disabled: false, href: '/agents' },
    {
      id: 'active',
      name: 'Running',
      icon: <PlayCircle size={12} />,
      disabled: !orgHasAgents,
      href: '/agents?state=active',
    },
    {
      id: 'paused',
      name: 'Paused',
      icon: <PauseCircle size={12} />,
      disabled: !orgHasAgents,
      href: '/agents?state=paused',
    },
    {
      id: 'draft',
      name: 'Drafts',
      icon: <FileText size={12} />,
      disabled: !orgHasAgents,
      href: '/agents?state=draft',
    },
  ];

  return (
    <Flex vertical style={{ height: `calc(100dvh - ${insets.top}px)`, minHeight: 0 }}>
      <Flex
        align={isSideNav ? 'center' : 'flex-end'}
        justify="space-between"
        gap={16}
        style={{
          padding: isMobile
            ? `8px 12px ${isSideNav ? 12 : 0}px`
            : hasSidebarGutter
              ? '16px 24px 8px 0'
              : '0 24px',
          minHeight: isMobile || isSideNav ? undefined : density === 'compact' ? 40 : 44,
          flexShrink: 0,
          backgroundColor: 'var(--ant-color-bg-layout)',
          position: 'relative',
        }}
      >
        {isSideNav ? (
          <>
            <GlobalSearch organizationId={selectedOrganization.id} />
            <SidebarSection
              title="STATUS"
              createLabel="Create agent"
              onCreate={canCreateAgents ? handleNewAgent : undefined}
              items={statusOptions}
              activeId={filterValue}
              onSelect={(option) => handleStatusChange(option.id)}
            >
              <Flex vertical gap={3}>
                {statusOptions.map((option) => (
                  <NavbarTab
                    key={option.id}
                    mode="side"
                    isActive={option.id === filterValue}
                    disabled={option.disabled}
                    href={option.href}
                    onClick={() => handleStatusChange(option.id)}
                  >
                    {option.icon}
                    <Typography.Text className="navbar-tab__label">{option.name}</Typography.Text>
                  </NavbarTab>
                ))}
              </Flex>
            </SidebarSection>
          </>
        ) : (
          <ConfigProvider theme={toolbarTheme}>
            <NavbarTabsOverflow
              title="MORE STATUSES"
              items={statusOptions}
              activeId={filterValue}
              onSelect={(option) => handleStatusChange(option.id)}
            />
          </ConfigProvider>
        )}
        <Flex align="center" gap={8} style={{ marginBottom: isSideNav ? 0 : 6 }}>
          {isSideNav && <HeaderActions organizationId={selectedOrganization.id} variant="side" />}
          <DateRangeFilter
            presetKey={datePreset?.key}
            from={dateFrom}
            to={dateTo}
            onPresetChange={(nextPreset) =>
              navigate({ search: { state, sortField, sortDirection, datePreset: nextPreset } })
            }
            onRangeChange={(rangeFrom, rangeTo) =>
              navigate({
                search: {
                  state,
                  sortField,
                  sortDirection,
                  fromDate: rangeFrom.format('YYYY-MM-DD'),
                  toDate: rangeTo.format('YYYY-MM-DD'),
                },
              })
            }
          />
          {canCreateAgents ? (
            <Button type="primary" onClick={handleNewAgent} size="medium">
              Create Agent
            </Button>
          ) : null}
        </Flex>
      </Flex>
      <TableSurface
        className={`table-surface--page-inset${isSideNav ? '' : ' table-surface--page-inset-top-nav'}`}
      >
        <Flex vertical flex={1} style={{ position: 'relative', minHeight: 0 }}>
          {isAgentsFilteredEmpty ? (
            <Flex justify="center" align="center" flex={1} style={{ padding: 24 }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Flex vertical align="center" gap={8} style={{ maxWidth: 400 }}>
                    <Typography.Title level={4} style={{ margin: 0 }}>
                      No results found
                    </Typography.Title>
                    <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                      No agents match the current filters or sort. Clear filters to see all agents
                      in your workspace.
                    </Typography.Paragraph>
                  </Flex>
                }
              >
                <Button type="primary" onClick={handleClearAgentsFilters}>
                  Clear filters
                </Button>
              </Empty>
            </Flex>
          ) : (
            <>
              {showAgentsNoDataFull ? null : (
                <Flex
                  vertical
                  flex={1}
                  className={isEmptyPreview ? 'table-surface__empty-preview-dim' : undefined}
                  style={{ minHeight: 0 }}
                >
                  <AgentTable
                    dataSource={displayAgents}
                    isPending={tableIsPending}
                    isError={isError}
                    isEmptyPreview={isEmptyPreview}
                    canEdit={canEditAgents}
                    onUpdateAiAgent={handleUpdateAiAgent}
                    onEnableWebhook={handleEnableWebhook}
                    onDisableWebhook={handleDisableWebhook}
                    webhookBusyAgentId={webhookBusyAgentId}
                    onLoadMore={handleLoadMore}
                    onNameClick={handleNameClick}
                    onSort={handleSort}
                    onClearSort={handleClearSort}
                    sortState={
                      sortField && sortDirection
                        ? { field: sortField, direction: sortDirection }
                        : null
                    }
                    selectedRows={selectedRows}
                    onSelectedRowsChange={handleSelectedRowsChange}
                  />
                </Flex>
              )}
              {isEmptyPreview || showAgentsNoDataFull ? (
                <CrmEmptyStateLayout
                  title="No agents yet"
                  body="You have not created any AI agents yet. Create your first agent to automate follow-ups and workflows."
                  overlay
                >
                  <Flex justify="center">
                    <Button type="primary" disabled={!canCreateAgents} onClick={handleNewAgent}>
                      Create Agent
                    </Button>
                  </Flex>
                </CrmEmptyStateLayout>
              ) : null}
            </>
          )}
        </Flex>
      </TableSurface>

      {!isAiPanelOpen && (
        <SelectionActionsBar
          entity="agent"
          count={selectedCount}
          onAction={panel.newChat}
          onClear={() => handleSelectedRowsChange(new Set())}
          actions={{
            edit: {
              hidden: !canEditAgents,
              onClick: () =>
                selectedAgents[0] &&
                navigate({ to: '/agents/$agentId', params: { agentId: selectedAgents[0].id } }),
            },
            runs: runsAction.action,
            pause: {
              hidden: !canEditAgents || !selectedAgents.some((agent) => agent.state === 'active'),
              onClick: () => handleSetAgentsState('paused'),
            },
            resume: {
              hidden: !canEditAgents || !selectedAgents.some((agent) => agent.state !== 'active'),
              onClick: () => handleSetAgentsState('active'),
            },
            delete: { hidden: !canDeleteAgents, onClick: handleBulkArchiveAgents },
          }}
        />
      )}

      {runsAction.drawer}
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_app/agents/')({
  component: AgentsIndexRoute,
  validateSearch: (params) => AgentsRouteSearchParamsSchema.parse(params),
});
